import ReactGA from "react-ga4";
import { useEffect, useRef } from "react";
import { useNavigation, useRoute } from "@react-navigation/native";
import { store } from "../redux/store/setup";
import getEnvironmentConfig from "./analytics.config";

// Get environment configuration
const envConfig = getEnvironmentConfig();

// Initialize Analytics with environment-specific settings
export const initAnalytics = () => {
  if (!envConfig.measurementId) {
    console.warn("Google Analytics measurement ID is missing!");
    return;
  }

  ReactGA.initialize(envConfig.measurementId, {
    gaOptions: {
      debug_mode: envConfig.debugMode,
      // Send environment as custom dimension
      custom_map: {
        dimension1: 'environment'
      }
    },
    gtagOptions: {
      debug_mode: envConfig.debugMode
    }
  });

  // Set environment parameter for all events
  ReactGA.set({
    environment: envConfig.environment,
    app_version: '1.1.1' // From package.json
  });

  console.log(`✅ Analytics initialized for ${envConfig.environment} environment`);
  console.log(`📊 Measurement ID: ${envConfig.measurementId}`);
  console.log(`🐛 Debug mode: ${envConfig.debugMode ? 'ON' : 'OFF'}`);
};

// Generic GA4 Event with automatic environment tagging
export const trackEvent = (eventName, params = {}) => {
  if (!eventName) {
    console.warn("GA4 event missing event name");
    return;
  }

  // Get user details from store
  const state = store.getState();
  const user = state.authReducer?.profile?.data?.appUser;
  const userParams = user ? { app_user_id: user.opbrix_id } : {};

  // Automatically add environment and user ID to all events
  const enrichedParams = {
    ...params,
    ...userParams,
    environment: envConfig.environment
  };

  // Add debug_mode parameter if in debug mode (for DebugView)
  if (envConfig.debugMode) {
    enrichedParams.debug_mode = true;
  }

  ReactGA.event(eventName, enrichedParams);

  if (envConfig.debugMode) {
    console.log(`📊 GA4 Event: ${eventName}`, enrichedParams);
  }
};

// Page View Tracking
export const trackPageView = (path, title = '') => {
  if (!path) return;

  const state = store.getState();
  const user = state.authReducer?.profile?.data?.appUser;

  // Use trackEvent to ensure all global params (like app_user_id) are included
  trackEvent("page_view", {
    page_path: path,
    page_title: title || path,
    app_user_id: user?.opbrix_id
  });

  if (envConfig.debugMode) {
    console.log(`📄 Page View: ${path}`);
  }
};

// Track time on screen
export const usePageTracking = () => {
  const route = useRoute();
  const navigation = useNavigation();
  const startTimeRef = useRef(Date.now());

  useEffect(() => {
    const pageName = route.name;
    const params = route.params ? JSON.stringify(route.params) : "";

    // Track page view
    trackPageView(pageName + params, pageName);
    startTimeRef.current = Date.now();

    return () => {
      const timeSpent = Math.round((Date.now() - startTimeRef.current) / 1000);
      trackEvent("time_on_screen", {
        screen_name: pageName,
        time_spent_sec: timeSpent,
      });
    };
  }, [route.name, route.params, navigation]);
};

// Track API calls
export const trackApiCall = (endpoint, duration, success) => {
  trackEvent("api_call", {
    endpoint,
    success,
    duration_ms: duration,
  });
};

// Track user type
export const trackUserType = (userType) => {
  trackEvent("user_type", {
    user_type: userType,
  });

  // Also set as user property
  ReactGA.set({ user_type: userType });
};

// Auth events
export const trackLogin = (method) => {
  trackEvent("login", {
    method,
  });
};

export const trackLogout = () => {
  trackEvent("logout", {});
};

// Navigation Tracking
export const trackNavigation = (fromPage, toPage) => {
  trackEvent("navigation", {
    from_page: fromPage,
    to_page: toPage,
  });
};

// Form Tracking
export const trackFormSubmit = (formName, success, timeTakenMs) => {
  trackEvent("form_submit", {
    form_name: formName,
    success,
    time_taken_ms: timeTakenMs,
  });
};

// Form Start Tracking (NEW)
export const trackFormStart = (formName) => {
  trackEvent("form_start", {
    form_name: formName,
  });
};

// Form Field Interaction (NEW)
export const trackFormFieldInteraction = (formName, fieldName) => {
  trackEvent("form_field_interaction", {
    form_name: formName,
    field_name: fieldName,
  });
};

// Error Tracking
export const trackError = (errorType, message, context = {}) => {
  trackEvent("error", {
    error_type: errorType,
    message,
    ...context
  });
};

// Button Click Tracking (IMPROVED)
export const trackButtonClick = (buttonName, buttonLocation = '', additionalData = {}) => {
  const state = store.getState();
  const user = state.authReducer?.profile?.data?.appUser;
  const userDetails = user ? {
    user_type: user.user_type
  } : {};

  trackEvent("button_click", {
    button_name: buttonName,
    button_location: buttonLocation,
    ...userDetails,
    ...additionalData
  });
};
export const buttonPressEvent = (buttonName, buttonLocation = '') => {
  const state = store.getState();
  const user = state.authReducer?.profile?.data?.appUser?.opbrix_id;

  trackEvent("button_press", {
    button_name: buttonName,
    button_location: buttonLocation,
    app_user_id: user
  });
};

// CTA Click Tracking (NEW - for important CTAs)
export const trackCTAClick = (ctaName, ctaType = 'primary', ctaLocation = '') => {
  trackEvent("cta_click", {
    cta_name: ctaName,
    cta_type: ctaType, // primary, secondary, tertiary
    cta_location: ctaLocation,
  });
};

// Link Click Tracking (NEW)
export const trackLinkClick = (linkText, linkUrl, linkLocation = '') => {
  trackEvent("link_click", {
    link_text: linkText,
    link_url: linkUrl,
    link_location: linkLocation,
  });
};

// Search Tracking (NEW)
export const trackSearch = (searchTerm, searchCategory = '', resultsCount = 0) => {
  trackEvent("search", {
    search_term: searchTerm,
    search_category: searchCategory,
    results_count: resultsCount,
  });
};

// Property Interaction Tracking (NEW - specific to your app)
export const trackPropertyView = (propertyId, propertyType = '') => {
  trackEvent("property_view", {
    property_id: propertyId,
    property_type: propertyType,
  });
};

export const trackPropertySave = (propertyId, propertyType = '') => {
  trackEvent("property_save", {
    property_id: propertyId,
    property_type: propertyType,
  });
};

export const trackPropertyUnsave = (propertyId, propertyType = '') => {
  trackEvent("property_unsave", {
    property_id: propertyId,
    property_type: propertyType,
  });
};

export const trackPropertyShare = (propertyId, shareMethod = '') => {
  trackEvent("property_share", {
    property_id: propertyId,
    share_method: shareMethod, // email, whatsapp, copy_link, etc.
  });
};

// Filter/Sort Tracking (NEW)
export const trackFilterApplied = (filterType, filterValue) => {
  trackEvent("filter_applied", {
    filter_type: filterType,
    filter_value: filterValue,
  });
};

export const trackSortApplied = (sortType) => {
  trackEvent("sort_applied", {
    sort_type: sortType,
  });
};

// Video/Media Tracking (NEW)
export const trackVideoPlay = (videoId, videoTitle = '') => {
  trackEvent("video_play", {
    video_id: videoId,
    video_title: videoTitle,
  });
};

export const trackVideoComplete = (videoId, videoTitle = '', watchTime = 0) => {
  trackEvent("video_complete", {
    video_id: videoId,
    video_title: videoTitle,
    watch_time_sec: watchTime,
  });
};

// Tab/Menu Navigation (NEW)
export const trackTabClick = (tabName, tabLocation = '') => {
  trackEvent("tab_click", {
    tab_name: tabName,
    tab_location: tabLocation,
  });
};

export const trackMenuOpen = (menuName) => {
  trackEvent("menu_open", {
    menu_name: menuName,
  });
};

export const trackMenuItemClick = (menuName, menuItem) => {
  trackEvent("menu_item_click", {
    menu_name: menuName,
    menu_item: menuItem,
  });
};

// Identify User
export const identifyUser = (userId, userProperties = {}) => {
  ReactGA.set({
    user_id: userId,
    ...userProperties
  });

  trackEvent("user_identified", {
    user_id: userId
  });
};

// E-commerce Events (if applicable)
export const trackAddToCart = (itemId, itemName, price, currency = 'GBP') => {
  trackEvent("add_to_cart", {
    currency: currency,
    value: price,
    items: [{
      item_id: itemId,
      item_name: itemName,
      price: price
    }]
  });
};

export const trackPurchase = (transactionId, value, currency = 'GBP', items = []) => {
  trackEvent("purchase", {
    transaction_id: transactionId,
    value: value,
    currency: currency,
    items: items
  });
};

// Export environment config for use in components
export { envConfig };